import React from "react";
import {
  ActivityIndicator,
  Image,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { knexo_logo } from "../../../constants/icons";
import { ForgotPasswordViewProps } from "../auth.types";

export default function ForgotPasswordView({
  email,
  isProcessing,
  emailSent,
  error,
  onEmailChange,
  onSendReset,
  onClose,
}: ForgotPasswordViewProps) {
  return (
    <KeyboardAvoidingView
      style={styles.wrap}
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      testID="forgot-password-screen"
    >
      <View style={styles.card}>
        <TouchableOpacity
          onPress={onClose}
          style={styles.backButton}
          testID="back-button"
        >
          <Text style={styles.backButtonText}>← Back to Sign In</Text>
        </TouchableOpacity>

        <Text style={styles.welcomeTitle}>
          {emailSent ? "Check Your Email" : "Reset Password"}
        </Text>

        <Image source={knexo_logo} style={styles.logo} resizeMode="contain" />

        {emailSent ? (
          <>
            <Text style={styles.description}>
              We've sent a password reset link to {email}. Please check your
              email and follow the instructions to reset your password.
            </Text>

            <Text style={styles.subtitle}>
              Didn't receive the email? Check your spam folder or try again with
              a different email address.
            </Text>

            <TouchableOpacity onPress={onClose} style={styles.btn}>
              <Text style={styles.btnText}>Back to Login</Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.description}>
              Enter your email address and we'll send you a link to reset your
              password.
            </Text>

            <Text style={styles.label}>Email Address</Text>
            <TextInput
              style={styles.input}
              value={email}
              onChangeText={onEmailChange}
              placeholder="Enter your email"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              editable={!isProcessing}
              testID="forgot-password-email-input"
            />

            {error ? (
              <Text style={styles.errorText} testID="forgot-password-error">
                {error}
              </Text>
            ) : null}

            <TouchableOpacity
              onPress={onSendReset}
              disabled={isProcessing || !email.trim()}
              style={[
                styles.btn,
                (!email.trim() || isProcessing) && styles.btnDisabled,
              ]}
            >
              {isProcessing ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.btnText}>Send Reset Link</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity onPress={onClose} style={styles.cancelButton}>
              <Text style={styles.cancelText}>Cancel</Text>
            </TouchableOpacity>
          </>
        )}
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    backgroundColor: "#f5f5f5",
    padding: 24,
    justifyContent: "center",
  },
  card: {
    alignSelf: "center",
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: 24,
    gap: 10 as unknown as number,
    shadowColor: "#000",
    shadowOpacity: 0.08,
    shadowRadius: 24,
    shadowOffset: { width: 0, height: 8 },
    elevation: 6,
  },
  backButton: {
    alignSelf: "flex-start",
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginBottom: 8,
  },
  backButtonText: { fontSize: 14, color: "#1e88e5", fontWeight: "600" },
  welcomeTitle: {
    textAlign: "center",
    marginBottom: 12,
    color: "#333",
    fontSize: 22,
    fontWeight: "700",
  },
  logo: {
    width: 300,
    height: 120,
    alignSelf: "center",
    marginBottom: 24,
  },
  description: {
    fontSize: 14,
    marginBottom: 20,
    color: "#333",
    lineHeight: 20,
    textAlign: "center",
  },
  subtitle: {
    fontSize: 12,
    color: "#666",
    marginBottom: 20,
    lineHeight: 16,
    textAlign: "center",
  },
  label: { fontSize: 12, color: "#666", marginTop: 8 },
  input: {
    height: 44,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#ddd",
    paddingHorizontal: 12,
    fontSize: 14,
    backgroundColor: "#fff",
    marginTop: 4,
  },
  errorText: {
    marginTop: 4,
    color: "#d32f2f",
    fontSize: 12,
    fontWeight: "500",
  },
  btn: {
    marginTop: 12,
    height: 48,
    backgroundColor: "#1e88e5",
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  btnDisabled: { opacity: 0.6 },
  btnText: { color: "#fff", fontWeight: "700", fontSize: 16 },
  cancelButton: {
    marginTop: 16,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 8,
  },
  cancelText: { fontSize: 14, color: "#666" },
});
